import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import FaqSection from '../components/FaqSection';


const Contact: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [queryType, setQueryType] = useState<'general' | 'product'>('general');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);


  const products = [
    { value: 'product-a', label: 'Product A' },
    { value: 'product-b', label: 'Product B' },
    { value: 'product-c', label: 'Product C' },
  ];


  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };


  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!message.trim()) {
      newErrors.message = 'Message is required';
    } else if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    if (queryType === 'product' && !selectedProduct) {
      newErrors.product = 'Please select a product for product-specific queries';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleBlur = (field: string, value: string) => {
    const newErrors = { ...errors };
    
    if (field === 'email' && value && !validateEmail(value)) {
      newErrors.email = "Please enter a valid email address";
    } else if (field === 'email' && value) {
      delete newErrors.email;
    }
    
    if (field === 'name' && value && value.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (field === 'name' && value) {
      delete newErrors.name;
    }
    
    if (field === 'message' && value && value.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (field === 'message' && value) {
      delete newErrors.message;
    }
    
    setErrors(newErrors);
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setFormError('Please fix the errors above before submitting.');
      return;
    }

    setFormError('');
    setIsSending(true);

    const payload = {
      name,
      email,
      message,
      queryType,
      product: selectedProduct || 'N/A',
    };

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to send email');
      }

      setSubmitted(true);
      setName('');
      setEmail('');
      setMessage('');
      setSelectedProduct('');
      setErrors({});
      
      // Auto-reset after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Email sending error:', err);
      setFormError('Failed to send message. Please try again later.');
    } finally {
      setIsSending(false);
    }
  };


  if (submitted) {
    return (
      <div className="container mx-auto px-6 max-w-6xl min-h-[60vh] flex flex-col items-center justify-center text-center pt-32">
        <div className="w-24 h-24 bg-green-50/20 rounded-full flex items-center justify-center text-green-500 mb-8 border border-green-100/20 transition-theme">
          <CheckCircle2 size={48} />
        </div>
        <h1 className="text-4xl font-bold mb-4 text-[var(--text-main)] transition-theme">Message Sent!</h1>
        <p className="text-[var(--text-muted)] max-w-sm transition-theme">
          Thank you for reaching out. A member of the PreciQube team will get back to you within 48 hours.
        </p>
        <button 
          onClick={() => setSubmitted(false)}
          className="mt-8 text-[var(--accent-blue)] font-bold hover:underline transition-all"
        >
          Send another message 
        </button>
      </div>
    );
  }


  return (
    <div className="container mx-auto px-6 max-w-6xl pt-32">
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold mb-6 text-[var(--text-main)] transition-theme">Contact Us</h1>
        <p className="text-[var(--text-muted)] transition-theme">
          Have a question or interested in collaboration? We'd love to hear from you.
        </p>
      </div>


      <div className="glass-card rounded-[3rem] p-8 md:p-16 relative overflow-hidden transition-theme">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-[var(--accent-blue)]"></div>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-bold text-[var(--text-muted)] ml-1 transition-theme uppercase tracking-wide">
                Name
              </label>
              <input 
                required
                type="text" 
                id="name"
                name="name"
                autoComplete="name"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={(e) => handleBlur('name', e.target.value)}
                disabled={isSending}
                aria-describedby={errors.name ? "name-error" : undefined}
                className={`w-full px-6 py-4 rounded-2xl bg-[var(--bg-main)] border ${
                  errors.name ? 'border-red-500' : 'border-[var(--card-border)]'
                } text-[var(--text-main)] focus:border-[var(--accent-blue)] focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:opacity-40 disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              {errors.name && (
                <p className="text-sm text-red-500 ml-1" id="name-error" role="alert">
                  {errors.name}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-bold text-[var(--text-muted)] ml-1 transition-theme uppercase tracking-wide">
                Email Address
              </label>
              <input 
                required
                type="email" 
                id="email"
                name="email"
                autoComplete="email"
                placeholder="hello@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => handleBlur('email', e.target.value)}
                disabled={isSending}
                aria-describedby={errors.email ? "email-error" : undefined}
                className={`w-full px-6 py-4 rounded-2xl bg-[var(--bg-main)] border ${
                  errors.email ? 'border-red-500' : 'border-[var(--card-border)]'
                } text-[var(--text-main)] focus:border-[var(--accent-blue)] focus:ring-4 focus:ring-blue-500/10 outline-none transition-all placeholder:opacity-40 disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              {errors.email && (
                <p className="text-sm text-red-500 ml-1" id="email-error" role="alert">
                  {errors.email}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <label htmlFor="queryType" className="text-sm font-bold text-[var(--text-muted)] ml-1 transition-theme uppercase tracking-wide">
                Type of Query
              </label>
              <select
                id="queryType"
                value={queryType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setQueryType(e.target.value as 'general' | 'product');
                  if (e.target.value === 'general') {
                    setSelectedProduct('');
                    const newErrors = { ...errors };
                    delete newErrors.product;
                    setErrors(newErrors);
                  }
                }}
                disabled={isSending}
                className="w-full px-6 py-4 rounded-2xl bg-[var(--bg-main)] border border-[var(--card-border)] text-[var(--text-main)] focus:border-[var(--accent-blue)] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="general">General Query</option>
                <option value="product">Product-specific Query</option>
              </select>
            </div>


            {queryType === 'product' && (
              <div className="space-y-2">
                <label htmlFor="product" className="text-sm font-bold text-[var(--text-muted)] ml-1 transition-theme uppercase tracking-wide">
                  Select Product
                </label>
                <select
                  id="product"
                  value={selectedProduct}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setSelectedProduct(e.target.value);
                    if (e.target.value) {
                      const newErrors = { ...errors };
                      delete newErrors.product;
                      setErrors(newErrors);
                    }
                  }}
                  disabled={isSending}
                  aria-describedby={errors.product ? "product-error" : undefined}
                  className={`w-full px-6 py-4 rounded-2xl bg-[var(--bg-main)] border ${
                    errors.product ? 'border-red-500' : 'border-[var(--card-border)]'
                  } text-[var(--text-main)] focus:border-[var(--accent-blue)] outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <option value="">-- Choose a product --</option>
                  {products.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                {errors.product && (
                  <p className="text-sm text-red-500 ml-1" id="product-error" role="alert">
                    {errors.product}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="message" className="text-sm font-bold text-[var(--text-muted)] ml-1 transition-theme uppercase tracking-wide">
              Message
            </label>
            <textarea 
              required
              id="message"
              rows={6}
              placeholder="Tell us about your interest or inquiry..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onBlur={(e) => handleBlur('message', e.target.value)}
              disabled={isSending}
              aria-describedby={errors.message ? "message-error" : undefined}
              className={`w-full px-6 py-4 rounded-2xl bg-[var(--bg-main)] border ${
                errors.message ? 'border-red-500' : 'border-[var(--card-border)]'
              } text-[var(--text-main)] focus:border-[var(--accent-blue)] focus:ring-4 focus:ring-blue-500/10 outline-none transition-all resize-none placeholder:opacity-40 disabled:opacity-50 disabled:cursor-not-allowed`}
            ></textarea>
            {errors.message && (
              <p className="text-sm text-red-500 ml-1" id="message-error" role="alert">
                {errors.message}
              </p>
            )}
          </div>
          {formError && (
            <p className="text-sm text-red-500" role="alert">{formError}</p>
          )}
          <p className="text-xs text-[var(--text-muted)] italic transition-theme">
            Note: PreciQube is deliberate in its communications. You'll hear back within 48 hours.
          </p>
          <button 
            type="submit"
            disabled={isSending}
            className="w-full md:w-auto px-12 py-5 bg-[var(--accent-blue)] text-white rounded-2xl font-bold shadow-xl shadow-blue-500/20 hover:brightness-110 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSending ? 'Sending...' : 'Send Message'} <Send size={20} />
          </button>
        </form>
      </div>


      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="p-10 rounded-[2.5rem] bg-[var(--accent-blue-soft)]/20 border border-[var(--accent-blue)]/10 transition-theme backdrop-blur-sm">
          <h4 className="font-bold mb-4 text-[var(--text-main)]">Email Inquiries</h4>
          <p className="text-[var(--text-muted)] mb-2">Primary: prakhar.ph20@alumni.iitm.ac.in</p>
          <p className="text-[var(--text-muted)]">CC: ph20b007@alumni.iitm.ac.in</p>
        </div>
        <div className="p-10 rounded-[2.5rem] bg-[var(--accent-blue-soft)]/20 border border-[var(--accent-blue)]/10 transition-theme backdrop-blur-sm">
          <h4 className="font-bold mb-4 text-[var(--text-main)]">Location</h4>
          <p className="text-[var(--text-muted)] mb-2">Indian Institute of Technology Madras</p>
          <p className="text-[var(--text-muted)]">Chennai, Tamil Nadu, 600036</p>
        </div>
      </div>

      <FaqSection fullBleed />
    </div>
  );
};


export default Contact;
